import React, { useState, useEffect } from 'react';
import { X, User, Shield, Calendar, Save, ArrowRight } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../UI/LoadingSpinner';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserAdded: () => void;
}

interface ServiceLicense {
  pk: number;
  name: string;
  description: string;
  category: string;
  checked: boolean;
  available: boolean;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onUserAdded }) => {
  const [currentStep, setCurrentStep] = useState<'profile' | 'licenses'>('profile');
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<ServiceLicense[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    mobile: '',
    admin_group: false,
    billing_group: false,
    technical_group: false,
    time_zone: 'US/Eastern',
  });

  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const timezones = [
    'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific', 'US/Alaska', 'US/Hawaii',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'
  ];

  useEffect(() => {
    if (isOpen) {
      loadAvailableServices();
    }
  }, [isOpen]);

  const loadAvailableServices = async () => {
    try {
      const response = await apiService.getServices();
      if (response.result) {
        // Filter only Voice360 services (itp_voice)
        const voice360Services = response.result.filter((service: any) => 
          service.item_type === 'itp_voice'
        );
        
        const servicesWithDefaults = voice360Services.map((service: any) => ({
          pk: service.pk,
          name: service.product.name,
          description: service.product.description,
          category: service.product.category,
          checked: false,
          available: true,
        }));
        setServices(servicesWithDefaults);
      }
    } catch (err: any) {
      console.error('Error loading services:', err);
      setError('Failed to load available services');
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.firstname.trim()) {
      errors.firstname = 'First name is required';
    }
    
    if (!formData.lastname.trim()) {
      errors.lastname = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: checkbox.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleServiceToggle = (serviceId: number) => {
    setServices(prev => prev.map(service => 
      service.pk === serviceId 
        ? { ...service, checked: !service.checked }
        : service
    ));
  };

  const handleNext = () => {
    if (currentStep === 'profile') {
      if (validateForm()) {
        setCurrentStep('licenses');
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Get current user account info
      const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
      const accountId = userDetails.account_id || 1;

      // Create the user
      const userData = {
        ...formData,
        account_id: accountId,
      };

      const createResponse = await apiService.createUser(userData);
      
      if (createResponse.result) {
        const newUserId = createResponse.result.pk;

        // Add selected licenses
        const selectedServices = services.filter(service => service.checked);
        for (const service of selectedServices) {
          try {
            await apiService.addUserLicense(newUserId, service.pk);
          } catch (licenseError) {
            console.error('Error adding license:', licenseError);
            // Continue with other licenses even if one fails
          }
        }

        onUserAdded();
        onClose();
        resetForm();
      }
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstname: '',
      lastname: '',
      email: '',
      mobile: '',
      admin_group: false,
      billing_group: false,
      technical_group: false,
      time_zone: 'US/Eastern',
    });
    setCurrentStep('profile');
    setValidationErrors({});
    setError(null);
    setServices([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add New User</h2>
              <p className="text-sm text-gray-600">
                {currentStep === 'profile' ? 'Enter user information' : 'Assign service licenses'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${currentStep === 'profile' ? 'text-blue-600' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'profile' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
              }`}>
                1
              </div>
              <span className="font-medium">Profile</span>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400" />
            <div className={`flex items-center space-x-2 ${currentStep === 'licenses' ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'licenses' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
              }`}>
                2
              </div>
              <span className="font-medium">Licenses</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {currentStep === 'profile' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.firstname ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                  />
                  {validationErrors.firstname && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.firstname}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      validationErrors.lastname ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                  />
                  {validationErrors.lastname && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.lastname}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter mobile number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  name="time_zone"
                  value={formData.time_zone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Contact Authorizations
                </label>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="admin_group"
                      checked={formData.admin_group}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 text-purple-600 mr-1" />
                        <span className="text-sm font-medium text-gray-900">Account Authorized</span>
                      </div>
                      <p className="text-xs text-gray-500">Authorized to manage the account and contact ITP on all matters</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="billing_group"
                      checked={formData.billing_group}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-blue-600 mr-1" />
                        <span className="text-sm font-medium text-gray-900">Billing Authorized</span>
                      </div>
                      <p className="text-xs text-gray-500">Authorized to contact ITP regarding billing and invoices</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="technical_group"
                      checked={formData.technical_group}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-green-600 mr-1" />
                        <span className="text-sm font-medium text-gray-900">Technical Authorized</span>
                      </div>
                      <p className="text-xs text-gray-500">Authorized to approve technical changes and service modifications</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 'licenses' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Assign Voice360 Licenses</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Select the Voice360 services this user should have access to.
                </p>
              </div>

              {services.length === 0 ? (
                <div className="text-center py-8">
                  <LoadingSpinner />
                  <p className="text-gray-500 mt-2">Loading available services...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {services.map(service => (
                    <div key={service.pk} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={service.checked}
                        onChange={() => handleServiceToggle(service.pk)}
                        disabled={!service.available}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                            <div className="text-xs text-gray-500">{service.description}</div>
                          </div>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {service.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={currentStep === 'profile' ? handleClose : () => setCurrentStep('profile')}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {currentStep === 'profile' ? 'Cancel' : 'Back'}
          </button>
          
          <button
            onClick={currentStep === 'profile' ? handleNext : handleSubmit}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <LoadingSpinner size="small" />
            ) : (
              <>
                {currentStep === 'profile' ? (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create User
                  </>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
